import { useTranslation } from 'react-i18next';
import type { ProgramListItemDataFragment } from '../ProgramListing/ProgramListing.api';
import type { AssessmentRunsFormProps } from '../shared/AssessmentRunsFormContainer';
import AssessmentRunsFormContainer from '../shared/AssessmentRunsFormContainer';
import type { CreateAssessmentRunsMutation, CreateAssessmentRunsMutationVariables } from './CreateAssessmentRuns.api';
import { CreateAssessmentRunsDocument } from './CreateAssessmentRuns.api';
import client from '@/apolloClient';
import { success as showSuccessNotification, error as showErrorNotification } from '@/utils/notifications/actions';

export interface CreateAssessmentRunsProps {
    companyId: string;
    program: ProgramListItemDataFragment;
    onClose: () => void;
    onSuccess: () => void;
}

const CreateAssessmentRuns = ({ companyId, program, onClose, onSuccess }: CreateAssessmentRunsProps) => {
    const { t } = useTranslation('programManagementPage');

    const handleSubmit: AssessmentRunsFormProps['onSubmit'] = async formData => {
        try {
            const variables: CreateAssessmentRunsMutationVariables = {
                companyId,
                input: {
                    programId: program.id,
                    groupId: formData.groupId,
                    assessmentRuns: formData.assessmentRuns,
                },
            };

            const { data } = await client.mutate<CreateAssessmentRunsMutation>({
                mutation: CreateAssessmentRunsDocument,
                variables,
            });

            if (data?.createAssessmentRuns?.__typename === 'CreateAssessmentRunsSuccessfulResponse') {
                if (onSuccess) {
                    onSuccess();
                } else {
                    onClose();
                }
                showSuccessNotification({
                    message: t('assessmentRuns.notifications.successTitle'),
                    description: t('assessmentRuns.notifications.successDescription'),
                });
                return;
            }

            if (data?.createAssessmentRuns?.__typename === 'ErrorResponse') {
                const error = {
                    fields: data.createAssessmentRuns.fields,
                };
                showErrorNotification({
                    message: t('assessmentRuns.notifications.errorTitle'),
                    description: error.fields?.[0]?.message || t('assessmentRuns.notifications.errorDescription'),
                });
                throw error;
            }

            throw new Error('Unknown error creating assessment runs');
        } catch {
            throw new Error('An unexpected error occurred while creating assessment runs');
        }
    };
    return (
        <AssessmentRunsFormContainer
            mode="create"
            onSubmit={handleSubmit}
            onClose={onClose}
            program={program}
            companyId={companyId}
        />
    );
};

export default CreateAssessmentRuns;
